// Central route registry — the ONLY place routes are declared. Feature agents
// implement pages inside src/features/<area>/ and DO NOT touch this file, lib/api.ts,
// the auth context, or the shell. To add a screen, a feature agent adds its page
// file and (if a new path) asks the scaffold owner to register it here.
//
// Guard hierarchy: RequireAuth (session) → RequireAdmin (JWT role gate; non-admins
// get the 無權限 screen) → AppShell (nav + topbar) → feature <Outlet/>.
// Router basename follows Vite BASE_URL. On the dedicated admin host this is "",
// so deep links resolve from https://v3-admin-dev.ls-dg.net/.

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { RequireAdmin, RequireAuth } from './auth/RequireAuth';
import { RequireCommerceCapability } from './auth/RequireCommerceCapability';
import { AppShell } from './components/AppShell';
import { routerBasename } from './config';
import { COMMERCE_CAPABILITIES } from './platform/commerceCapabilities';

import LoginPage from './features/login/LoginPage';
import DashboardPage from './features/dashboard/DashboardPage';
import UsersPage from './features/users/UsersPage';
import UserDetailPage from './features/users/UserDetailPage';
import DevicesPage from './features/devices/DevicesPage';
import DeviceDetailPage from './features/devices/DeviceDetailPage';
import DiagnosticLocksPage from './features/diagnostics/DiagnosticLocksPage';
import AuditPage from './features/audit/AuditPage';
import DevReviewPage from './features/devreview/DevReviewPage';
import FirmwarePage from './features/firmware/FirmwarePage';
import ApiKeysPage from './features/apikeys/ApiKeysPage';
import CertificatesPage from './features/certificates/CertificatesPage';
import TeamPage from './features/team/TeamPage';
import CompliancePage from './features/compliance/CompliancePage';
import CompaniesPage from './features/companies/CompaniesPage';
import CustomerFamiliesPage from './features/customerFamilies/CustomerFamiliesPage';
import OrganizationsPage from './features/organizations/OrganizationsPage';
import AdminMechanismsPage from './features/operations/AdminMechanismsPage';
import PlatformSettingsPage from './features/platform/PlatformSettingsPage';
import ReportsPage from './features/reports/ReportsPage';
import MonitoringPage from './features/monitoring/MonitoringPage';
import ResetPasswordPage from './features/login/ResetPasswordPage';
import ConversationsPage from './features/ai-ops/ConversationsPage';
import GuardrailPage from './features/ai-ops/GuardrailPage';
import HandoffsPage from './features/ai-ops/HandoffsPage';
import KbGapsPage from './features/ai-ops/KbGapsPage';
import ButlerAuditPage from './features/ai-ops/ButlerAuditPage';
import AssistantSettingsPage from './features/assistant/AssistantSettingsPage';
import AiUsagePage from './features/ai-usage/AiUsagePage';
import PlansPage from './features/commerce/PlansPage';
import PlanGrantsPage from './features/commerce/PlanGrantsPage';
import OpsGovernancePage from './features/opsGovernance/OpsGovernancePage';
import AccountingPage from './features/commerce/AccountingPage';
import ReconciliationsPage from './features/commerce/ReconciliationsPage';
import EcosystemsPage from './features/ecosystem/EcosystemsPage';
import AidAllocationsPage from './features/aid/AidAllocationsPage';
import PoManagementPage from './features/aid/PoManagementPage';
import AidInventoryPage from './features/aid/AidInventoryPage';
import AidBatchesPage from './features/aid/AidBatchesPage';
import AidAnomaliesPage from './features/aid/AidAnomaliesPage';
import HelpdeskGovernancePage from './features/helpdesk/HelpdeskGovernancePage';

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename={routerBasename}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route element={<RequireAuth />}>
            <Route element={<RequireAdmin />}>
              <Route element={<AppShell />}>
                <Route index element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />
                <Route path="/devices" element={<DevicesPage />} />
                <Route path="/devices/:id" element={<DeviceDetailPage />} />
                <Route path="/diagnostics" element={<DiagnosticLocksPage />} />
                <Route path="/audit" element={<AuditPage />} />
                <Route path="/dev-reviews" element={<DevReviewPage />} />
                <Route path="/firmware" element={<FirmwarePage />} />
                <Route path="/api-keys" element={<ApiKeysPage />} />
                <Route path="/certificates" element={<CertificatesPage />} />
                <Route path="/team" element={<TeamPage />} />
                {/* /roles 併入 /team（維運團隊）；舊 deep link 轉向 */}
                <Route path="/roles" element={<Navigate to="/team" replace />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/ai/conversations" element={<ConversationsPage />} />
                <Route path="/ai/guardrail" element={<GuardrailPage />} />
                <Route path="/ai/handoffs" element={<HandoffsPage />} />
                <Route path="/ai/kb-gaps" element={<KbGapsPage />} />
                <Route path="/ai/butler-audit" element={<ButlerAuditPage />} />
                {/* AI 使用記錄(LLM 呼叫稽核;S41) */}
                <Route path="/ai-usage" element={<AiUsagePage />} />
                {/* AI 管理助理設定(AI 金鑰登錄台;S40) */}
                <Route path="/assistant-settings" element={<AssistantSettingsPage />} />
                <Route path="/organizations" element={<OrganizationsPage />} />
                {/* AID 供應鏈域(S28–S30,IA specs/01 §2.1;admin#3 P2)。/v3/admin/aids* 代理待落地→誠實佔位頁 */}
                <Route path="/aid/inventory" element={<AidInventoryPage />} />
                <Route path="/aid/batches" element={<AidBatchesPage />} />
                <Route path="/aid/anomalies" element={<AidAnomaliesPage />} />
                {/* PO 配給(S31/AL,ca-manager 為主):建 PO 即從池配滿 /v3/admin/pos* */}
                <Route path="/aid/pos" element={<PoManagementPage />} />
                {/* 開發者組織域 S31 AID 分配(restful#74 已在 main→live) */}
                <Route path="/aid-allocations" element={<AidAllocationsPage />} />
                {/* 方案與帳務域(S33–S36,IA specs/01 §2.3;admin#3 P1) */}
                <Route path="/plans" element={<RequireCommerceCapability capability={COMMERCE_CAPABILITIES.planCatalogRead}><PlansPage /></RequireCommerceCapability>} />
                <Route path="/plan-grants" element={<RequireCommerceCapability capability={COMMERCE_CAPABILITIES.planGrantRead}><PlanGrantsPage /></RequireCommerceCapability>} />
                <Route path="/ops-governance" element={<OpsGovernancePage />} />
                <Route path="/accounting" element={<RequireCommerceCapability capability={COMMERCE_CAPABILITIES.commerceRead}><AccountingPage /></RequireCommerceCapability>} />
                <Route path="/reconciliations" element={<RequireCommerceCapability capability={COMMERCE_CAPABILITIES.commerceRead}><ReconciliationsPage /></RequireCommerceCapability>} />
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/customer-families" element={<CustomerFamiliesPage />} />
                {/* 信任與治理域擴充:S37 生態系治理(IA specs/01 §2.4;admin#6) */}
                <Route path="/ecosystems" element={<EcosystemsPage />} />
                <Route path="/platform-settings" element={<PlatformSettingsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/operations" element={<AdminMechanismsPage />} />
                <Route path="/helpdesk-governance" element={<HelpdeskGovernancePage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
